import { Clock } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'

import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAuthStore } from '@/stores/auth-store'

export function SessionExpiryWarningDialog() {
  const { showExpiryWarning, setShowExpiryWarning, sessionExpired } = useAuthStore()
  const auth = useAuth()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  const open = showExpiryWarning && !sessionExpired

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
    }
    if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    setRefreshError(null)
    try {
      await auth.signinSilent()
      setShowExpiryWarning(false)
    } catch {
      setRefreshError('Unable to refresh session. Please sign in again.')
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDismiss = () => {
    setShowExpiryWarning(false)
  }

  const handleSignIn = () => {
    const returnUrl = window.location.pathname + window.location.search
    void auth.signinRedirect({ state: { returnUrl } })
  }

  if (!open) return null

  return (
    <dialog
      ref={dialogRef}
      onClose={handleDismiss}
      className="rounded-lg shadow-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-0 backdrop:bg-gray-900/60 max-w-sm w-full"
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Session Expiring Soon
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Your session could not be renewed automatically and will expire soon. Save any work in
          progress and refresh your session to continue.
        </p>
        {refreshError && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
            <p className="text-sm text-red-800 dark:text-red-200">{refreshError}</p>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          {refreshError ? (
            <Button onClick={handleSignIn}>Sign In</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleDismiss} disabled={isRefreshing}>
                Dismiss
              </Button>
              <Button onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing && <LoadingSpinner size="sm" className="mr-2" />}
                Refresh Session
              </Button>
            </>
          )}
        </div>
      </div>
    </dialog>
  )
}
